/**
 * Headings Transformer
 * Transforms WooRank headings criteria data into React component-ready format
 */

import {
  getStatus,
  getIconType,
  getImportance,
  getSolvability,
} from '../../constants/seoMappings';

/**
 * Heading tag order for consistent display
 */
const HEADING_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

/**
 * Checks if headings data is empty (no headings found)
 * @param {Object} data - The headings data object
 * @returns {boolean} - True if no headings exist
 */
const isHeadingsEmpty = (data) => {
  if (!data) return true;
  
  return HEADING_TAGS.every((tag) => {
    const tagData = data?.[tag] || [];
    return !tagData || tagData.length === 0;
  });
};

/**
 * Calculates the count of headings for each tag level
 * @param {Object} data - The headings data object
 * @returns {Object} - Object with counts for each heading level
 */
const calculateCounts = (data) => {
  const counts = {};
  
  HEADING_TAGS.forEach((tag) => {
    counts[tag] = data?.[tag]?.length || 0;
  });
  
  return counts;
};

/**
 * Flattens headings data into a single array with tag information
 * @param {Object} data - The headings data object
 * @returns {Array} - Flattened array of headings with their tags
 */
const flattenHeadings = (data) => {
  if (!data) return [];
  
  const flatHeadings = [];
  
  HEADING_TAGS.forEach((tag) => {
    const headingsForTag = data?.[tag] || [];
    headingsForTag.forEach((heading) => {
      flatHeadings.push({
        tag: tag?.toUpperCase() || '',
        heading: heading || '',
      });
    });
  });
  
  return flatHeadings;
};

/**
 * Generates a scenario-based message for the headings
 * @param {boolean} isEmpty - Whether no headings were found
 * @returns {string|null} - The appropriate message or null if headings exist
 */
const generateMessage = (isEmpty) => {
  if (isEmpty) {
    return 'No headings were found.';
  }
  return null; // No message needed when headings exist
};

/**
 * Transforms the headings criteria from WooRank API response
 * @param {Object} headingsCriteria - The headings criteria object from the API
 * @returns {Object} - Transformed headings data for React components
 */
export const transformHeadings = (headingsCriteria) => {
  // Handle missing criteria entirely
  if (!headingsCriteria) {
    return {
      status: 'error',
      iconType: 'error',
      importance: { level: 0, text: '' },
      solvability: { level: 0, text: '' },
      isEmpty: true,
      message: 'No headings were found.',
      data: null,
    };
  }

  const data = headingsCriteria?.data || null;
  const wooRankStatus = headingsCriteria?.status || 'bad';
  const importance = headingsCriteria?.importance || 0;
  const solvability = headingsCriteria?.solvability || 0;

  // Check if data is empty/missing
  const isEmpty = isHeadingsEmpty(data);

  return {
    status: getStatus(wooRankStatus),
    iconType: getIconType(wooRankStatus),
    importance: getImportance(importance),
    solvability: getSolvability(solvability),
    isEmpty,
    message: generateMessage(isEmpty),
    data: isEmpty ? null : {
      counts: calculateCounts(data) || {},
      headings: flattenHeadings(data) || [],
    },
  };
};

export default transformHeadings;

